import React, { useState, useEffect, useCallback } from 'react';
import { User, UserFilterState, UserMetricsSummary } from '@/types/domain/user';
import { mockUserManagementService } from '@/services/mock/MockUserManagementService';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { UserKpiCards } from '../users/components/UserKpiCards';
import { UserTable } from '../users/components/UserTable';
import { UserDetailDrawer } from '../users/components/UserDetailDrawer';
import { UserCreateModal } from '../users/components/UserCreateModal';
import { UserEditModal } from '../users/components/UserEditModal';
import { UserStatusActionModal, UserActionType } from '../users/components/UserStatusActionModal';
import { Button } from '@/components/ui/Button';
import { Users, RefreshCw, Plus, Download } from 'lucide-react';
import { showToast } from '@/context/NotificationContext';

export const AdminUsersPage: React.FC = () => {
  const { user: currentAdmin } = useAuth();

  const [metrics, setMetrics] = useState<UserMetricsSummary>({
    totalUsers: 0,
    activeUsers: 0,
    pendingInvitations: 0,
    blockedUsers: 0,
    inactiveUsers: 0,
    lockedUsers: 0,
  });

  const [filterState, setFilterState] = useState<UserFilterState>({
    search: '',
    status: 'ALL',
    role: 'ALL',
    department: 'ALL',
    plant: 'ALL',
  });

  const [refreshKey, setRefreshKey] = useState<number>(Date.now());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedUserForView, setSelectedUserForView] = useState<User | null>(null);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<User | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [statusActionModal, setStatusActionModal] = useState<{
    isOpen: boolean;
    user: User | null;
    actionType: UserActionType;
  }>({
    isOpen: false,
    user: null,
    actionType: 'DEACTIVATE',
  });

  // Fetch KPI Metrics
  const fetchMetrics = useCallback(async () => {
    try {
      const m = await mockUserManagementService.getUserMetrics();
      setMetrics(m);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Infinite Scroll Data Fetcher
  const fetchFn = useCallback(
    async (params: { page?: number; pageSize?: number }) => {
      const res = await mockUserManagementService.getUsers({
        ...filterState,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      });
      return {
        data: res.items,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        total: res.total,
        totalPages: Math.ceil(res.total / (params.pageSize || 25)),
        hasNextPage: (params.page || 1) * (params.pageSize || 25) < res.total,
        hasPreviousPage: (params.page || 1) > 1,
      };
    },
    [filterState]
  );

  const {
    items: users,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    total: totalCount,
    sentinelRef,
    retry,
  } = useInfiniteScroll<User>({
    fetchFn,
    pageSize: 25,
    params: { ...filterState, _key: refreshKey } as any,
  });

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchMetrics(), retry()]);
    setIsRefreshing(false);
    showToast('Directory Refreshed', 'Enterprise user directory updated.', 'info');
  };

  const handleExportCsv = () => {
    if (!users || users.length === 0) {
      showToast('Export Notice', 'No user records available to export.', 'info');
      return;
    }

    const headers = ['Employee ID', 'Name', 'Email', 'Role', 'Department', 'Plant', 'Status', 'Approval Limit (INR)'];
    const rows = users.map((u) => [
      u.employeeId,
      `"${u.name}"`,
      u.email,
      u.role,
      `"${u.departmentName}"`,
      u.plantCode,
      u.status,
      u.approvalAuthorityLimit,
    ]);

    const csvContent = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `procureai_users_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    showToast('Export Complete', `Exported ${users.length} enterprise user identities to CSV.`, 'success');
  };

  const handleFilterChange = (newFilters: Partial<UserFilterState>) => {
    setFilterState((prev) => ({ ...prev, ...newFilters }));
  };

  const handleSelectStatusKpi = (statusKey: string) => {
    setFilterState((prev) => ({
      ...prev,
      status: statusKey as any,
    }));
  };

  const handleUserCreated = (newUser: User) => {
    setMetrics((prev) => ({
      ...prev,
      totalUsers: prev.totalUsers + 1,
      pendingInvitations: prev.pendingInvitations + 1,
    }));

    setFilterState((prev) => ({
      ...prev,
      status: prev.status === 'INVITATION_PENDING' ? 'INVITATION_PENDING' : 'ALL',
      search: '',
    }));

    setRefreshKey(Date.now());
    fetchMetrics();
    showToast('Directory Updated', `${newUser.name} (${newUser.employeeId}) added to directory.`, 'success');
  };

  const handleUserUpdated = (updatedUser: User) => {
    fetchMetrics();
    setRefreshKey(Date.now());
    if (selectedUserForView && selectedUserForView.id === updatedUser.id) {
      setSelectedUserForView(updatedUser);
    }
  };

  const handleStatusSuccess = (updatedUser: User) => {
    fetchMetrics();
    setRefreshKey(Date.now());
    if (selectedUserForView && selectedUserForView.id === updatedUser.id) {
      setSelectedUserForView(updatedUser);
    }
  };

  const activeAdminCount = users.filter((u) => u.role === 'ADMIN' && u.status === 'ACTIVE').length;

  return (
    <div className="space-y-6 pb-12 font-poppins">
      {/* Top Header matching PurchaseOrdersListPage & VendorsListPage standard */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-foreground flex items-center gap-2.5 font-poppins">
            <Users className="h-7 w-7 text-primary" /> User Management & Enterprise Directory
          </h1>
          <p className="text-xs text-foreground-secondary font-medium mt-1 font-poppins">
            Centralized employee directory, Delegation of Authority (DOA) limits, plant access scopes, and identity security quarantine.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="rounded-xl font-bold font-poppins cursor-pointer"
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCsv}
            className="rounded-xl font-bold font-poppins cursor-pointer border-border"
          >
            <Download className="h-4 w-4 mr-1.5" /> Export CSV
          </Button>

          <Button
            size="sm"
            onClick={() => setIsCreateModalOpen(true)}
            className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-primary-foreground font-poppins cursor-pointer shadow-xs"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add User
          </Button>
        </div>
      </div>

      {/* KPI Metric Summary Cards Strip */}
      <UserKpiCards
        metrics={metrics}
        activeStatusFilter={filterState.status || 'ALL'}
        onSelectStatusFilter={handleSelectStatusKpi}
      />

      {/* Enterprise Users Table with Aligned Actions: View, Edit, Lock, Delete */}
      <UserTable
        users={users}
        totalCount={totalCount}
        filterState={filterState}
        onFilterChange={handleFilterChange}
        onViewUser={(u) => setSelectedUserForView(u)}
        onEditUser={(u) => setSelectedUserForEdit(u)}
        onLockUser={(u) =>
          setStatusActionModal({
            isOpen: true,
            user: u,
            actionType: u.status === 'LOCKED' ? 'UNLOCK' : u.status === 'BLOCKED' ? 'UNBLOCK' : 'BLOCK',
          })
        }
        onDeleteUser={(u) =>
          setStatusActionModal({
            isOpen: true,
            user: u,
            actionType: 'DEACTIVATE',
          })
        }
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={hasNextPage}
        sentinelRef={sentinelRef}
      />

      {/* Slide-Over Curtain Drawer for View Details */}
      <UserDetailDrawer
        isOpen={Boolean(selectedUserForView)}
        onClose={() => setSelectedUserForView(null)}
        user={selectedUserForView}
      />

      {/* User Create Modal (Compact, Zero-Scroll, Inline Validation) */}
      <UserCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onUserCreated={handleUserCreated}
        existingUsers={users}
        currentAdmin={currentAdmin}
      />

      {/* User Edit Modal */}
      <UserEditModal
        isOpen={Boolean(selectedUserForEdit)}
        onClose={() => setSelectedUserForEdit(null)}
        user={selectedUserForEdit}
        onUserUpdated={handleUserUpdated}
        existingUsers={users}
        currentAdmin={currentAdmin}
      />

      {/* Status Action Confirmation Modal (for Lock and Delete/Deactivate) */}
      <UserStatusActionModal
        isOpen={statusActionModal.isOpen}
        onClose={() => setStatusActionModal((prev) => ({ ...prev, isOpen: false }))}
        user={statusActionModal.user}
        actionType={statusActionModal.actionType}
        onSuccess={handleStatusSuccess}
        currentAdmin={currentAdmin}
        activeAdminCount={activeAdminCount}
      />
    </div>
  );
};
